/**
 * Scene-level sketch state — surface and screenspace strokes that live
 * independently of any individual SceneObject.
 *
 * Sketch mode is no longer bound 1:1 to a SceneObject. Layers and strokes are
 * scene-level constructs; each stroke records its own kind:
 *
 *  - "projected"  — surface-locked, stored in mesh-local bind-pose with baked
 *                   skin weights so it deforms with the host object's
 *                   skeleton. Each point carries its own attachment so one
 *                   stroke can flow across multiple surfaces.
 *  - "screenspace" — placed 1 unit from the camera at draw time, baked into
 *                    world coordinates. After creation the stroke is anchored
 *                    in world space (it does NOT follow the camera).
 *
 * Stroke positions are baked on creation. Projected strokes deform with their
 * host object's skeleton (skinned meshes) or transform (static meshes), but
 * do not re-fit if the host is later scaled, joined, or topology-mutated —
 * those ops drop or bake the stroke through `pruneSceneSketch`.
 */
import type { SketchToolType } from "../app/storeTypes.ts";

// ---------------------------------------------------------------------------
// Stroke samples
// ---------------------------------------------------------------------------

/** A single sample on a projected (surface-locked) stroke. */
export interface ProjectedStrokePoint {
  /** Surface host for this point. */
  attachment: {
    objectId: string;
    topologyVersion: number;
  };
  /** Mesh-local bind-pose position. */
  p: [number, number, number];
  /** Pen pressure 0..1, modulates per-point opacity. */
  pressure: number;
  /** Top-4 bone indices (skinned meshes only). Barycentric-merged from the
   *  hit triangle's corner skin attributes at draw time. */
  skinIndex?: [number, number, number, number];
  /** Normalised bone weights matching `skinIndex`. */
  skinWeight?: [number, number, number, number];
  /** Geometry vertex indices of the hit triangle, for morph-aware replay. */
  corners?: [number, number, number];
  /** Barycentric coordinates inside `corners`, for morph-aware replay. */
  bary?: [number, number, number];
}

/** A single sample on a screenspace (camera-anchored-at-draw-time) stroke. */
export interface ScreenspaceStrokePoint {
  /** World-space position, baked at the moment the sample was captured. */
  p: [number, number, number];
  /** Pen pressure 0..1, modulates per-point opacity. */
  pressure: number;
}

// ---------------------------------------------------------------------------
// Stroke kinds (discriminated union)
// ---------------------------------------------------------------------------

interface SketchStrokeBase {
  /** Stable id (e.g. "ps_xxx"). */
  id: string;
  /** Sketch tool used to create the stroke. */
  tool: SketchToolType;
  /** Stroke color as hex string. */
  color: string;
  /** Tool thickness at capture time, in CSS pixels (UI-friendly). */
  thickness: number;
  /** Rendered line width in world units, sampled at the moment the stroke
   *  began (so pre-cached `worldThickness` survives camera moves). */
  worldThickness?: number;
  /** Per-stroke multiplier 0..1. */
  opacity: number;
}

/**
 * Surface-locked stroke. Each point is in the bind-pose space of the
 * point's attached host; the renderer reconstructs world positions through
 * that host object's mesh matrix and (for skinned hosts) the skeleton.
 *
 * `fallbackPlane` is captured on pointer-down; subsequent samples that miss
 * every sketchable mesh are projected onto this fixed plane and attached to
 * the last sampled surface. Optional because some strokes never need it; the
 * renderer ignores it.
 */
export interface ProjectedStroke extends SketchStrokeBase {
  kind: "projected";
  fallbackPlane?: {
    point: [number, number, number];
    normal: [number, number, number];
  };
  points: ProjectedStrokePoint[];
}

/**
 * Camera-anchored-at-draw-time stroke. Points are in world space; placed 1
 * unit from the camera at the moment each sample was captured. After the
 * pointerup, the stroke does not move — it floats in world space at the
 * place the user drew it.
 */
export interface ScreenspaceStroke extends SketchStrokeBase {
  kind: "screenspace";
  points: ScreenspaceStrokePoint[];
}

export type SketchStroke = ProjectedStroke | ScreenspaceStroke;

/** Type-guard helpers (avoids inline `s.kind === ...` literals at call sites). */
export function isProjectedStroke(s: SketchStroke): s is ProjectedStroke {
  return s.kind === "projected";
}
export function isScreenspaceStroke(s: SketchStroke): s is ScreenspaceStroke {
  return s.kind === "screenspace";
}

// ---------------------------------------------------------------------------
// Layers and scene-level container
// ---------------------------------------------------------------------------

/** Named ordered collection of strokes, mixed projected + screenspace. */
export interface SketchLayer {
  /** Stable id (e.g. "pl_xxx"). */
  id: string;
  /** User-editable display name. Not required to be unique. */
  name: string;
  /** Visibility toggle — when false, none of the layer's strokes render. */
  visible: boolean;
  /** Multiplied into each stroke's opacity at render time. */
  opacity: number;
  /** Strokes in render order (later strokes draw over earlier ones). */
  strokes: SketchStroke[];
}

/** The whole sketch document — scene-level. */
export interface SceneSketchState {
  layers: SketchLayer[];
}

// ---------------------------------------------------------------------------
// Constructors
// ---------------------------------------------------------------------------

export function makeStrokeId(): string {
  return `ps_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export function makeSketchLayerId(): string {
  return `pl_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export function createSketchLayer(name: string): SketchLayer {
  return {
    id: makeSketchLayerId(),
    name,
    visible: true,
    opacity: 1,
    strokes: [],
  };
}

/** Empty scene sketch state — no layers. Layer 1 is auto-created on sketch
 *  mode entry (see `enterSketchMode` in `sketch/sceneSketchOps.ts`). */
export function createEmptySceneSketchState(): SceneSketchState {
  return { layers: [] };
}

// ---------------------------------------------------------------------------
// Cloning — strokes are plain JSON; deep-copy for undo / publish boundaries
// ---------------------------------------------------------------------------

function cloneTuple3<T extends [number, number, number]>(t: T): T {
  return [t[0], t[1], t[2]] as T;
}

function cloneTuple4<T extends [number, number, number, number]>(t: T): T {
  return [t[0], t[1], t[2], t[3]] as T;
}

function cloneProjectedPoint(pt: ProjectedStrokePoint): ProjectedStrokePoint {
  return {
    attachment: {
      objectId: pt.attachment.objectId,
      topologyVersion: pt.attachment.topologyVersion,
    },
    p: cloneTuple3(pt.p),
    pressure: pt.pressure,
    skinIndex: pt.skinIndex ? cloneTuple4(pt.skinIndex) : undefined,
    skinWeight: pt.skinWeight ? cloneTuple4(pt.skinWeight) : undefined,
    corners: pt.corners ? cloneTuple3(pt.corners) : undefined,
    bary: pt.bary ? cloneTuple3(pt.bary) : undefined,
  };
}

function cloneScreenspacePoint(
  pt: ScreenspaceStrokePoint,
): ScreenspaceStrokePoint {
  return {
    p: cloneTuple3(pt.p),
    pressure: pt.pressure,
  };
}

export function cloneStroke(s: SketchStroke): SketchStroke {
  if (s.kind === "projected") {
    return {
      kind: "projected",
      id: s.id,
      tool: s.tool,
      color: s.color,
      thickness: s.thickness,
      worldThickness: s.worldThickness,
      opacity: s.opacity,
      fallbackPlane: s.fallbackPlane
        ? {
            point: cloneTuple3(s.fallbackPlane.point),
            normal: cloneTuple3(s.fallbackPlane.normal),
          }
        : undefined,
      points: s.points.map(cloneProjectedPoint),
    };
  }
  return {
    kind: "screenspace",
    id: s.id,
    tool: s.tool,
    color: s.color,
    thickness: s.thickness,
    worldThickness: s.worldThickness,
    opacity: s.opacity,
    points: s.points.map(cloneScreenspacePoint),
  };
}

export function cloneLayer(layer: SketchLayer): SketchLayer {
  return {
    id: layer.id,
    name: layer.name,
    visible: layer.visible,
    opacity: layer.opacity,
    strokes: layer.strokes.map(cloneStroke),
  };
}

export function cloneSceneSketchState(s: SceneSketchState): SceneSketchState {
  return { layers: s.layers.map(cloneLayer) };
}
